/**
 * Runtime vocabulary arrays for the director's controlled enums.
 *
 * The shape and order here MUST match `server/src/types.ts` so the LLM's
 * accepted outputs and the client's UI dropdowns reference the same set
 * of strings. Anything that wants to render a `<select>` of emotions or
 * gestures imports from this file rather than hard-coding a list, so
 * adding/removing a vocab entry is a one-line change.
 *
 * Why a separate file rather than `types.ts`:
 *   - `types.ts` is pure type declarations (no runtime values), useful
 *     for type-checking but invisible at runtime.
 *   - Dropdowns / pickers / autocomplete need iterable arrays.
 *   - Promoting these to runtime here means the type and the array can
 *     never drift; the type is derived from the array.
 *
 * Keep in sync with:
 *   - server/src/types.ts (EMOTIONS, GAZE_DIRECTIONS, GESTURES,
 *     BODY_GESTURES, BODY_STATES)
 */

// ── Emotion vocabulary ────────────────────────────────────────────────────

export const EMOTIONS = [
  'joy',
  'sadness',
  'anger',
  'fear',
  'surprise',
  'disgust',
  'disbelief',
  'excitement',
  'calm',
  'bored',
  'tense',
  'tired',
  'confused',
  'defeated',
  'triumphant',
  'neutral',
] as const;

export type Emotion = (typeof EMOTIONS)[number];

// ── Gaze vocabulary ──────────────────────────────────────────────────────

export const GAZE_DIRECTIONS = [
  'forward',
  'down',
  'down_left',
  'down_right',
  'up',
  'up_left',
  'up_right',
  'left',
  'right',
  'away',
] as const;

export type GazeDirection = (typeof GAZE_DIRECTIONS)[number];

// ── Face gesture vocabulary ──────────────────────────────────────────────

export const GESTURES = [
  'gasp',
  'laugh',
  'chuckle',
  'wince',
  'flinch',
  'head_shake',
  'head_nod',
  'eyebrow_flash',
  'sigh',
  'yawn',
] as const;

export type Gesture = (typeof GESTURES)[number];

// ── Body gesture vocabulary ──────────────────────────────────────────────

export const BODY_GESTURES = [
  'shoulder_shrug',
  'shoulder_roll',
  'weight_shift',
  'glance_over_shoulder',
  'chest_inhale_deep',
  'chest_deflate',
  'arm_brace_self',
  'hand_to_neck_rub',
  'lean_in',
  'lean_back',
  'recoil_step',
  'flinch_body',
  'head_tilt_curious',
  'nervous_finger_fidget',
  'foot_tap',
] as const;

export type BodyGesture = (typeof BODY_GESTURES)[number];

// ── Body state vocabulary ─────────────────────────────────────────────────

export const BODY_STATES = [
  'neutral_idle',
  'restless_idle',
  'slumped_idle',
  'tense_idle',
  'confident_idle',
  'withdrawn_idle',
] as const;

export type BodyState = (typeof BODY_STATES)[number];
